import { typo } from '@design-system/styles'
import { css } from '@emotion/react'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { isMobile, isTablet } from 'react-device-detect'
import { matchPath, useHistory } from 'react-router'
import { NavLink } from 'react-router-dom'

import commonRepo from '@/repository/common.repo'
import { toastFailure } from '@/utils/toast'

type Props = {
  to: React.ComponentProps<NavLink>['to']
  /** 활성 판별에 쓸 경로. 한 탭이 여러 라우트를 포괄하면 배열로 전달한다. */
  matchUrl?: string | string[]
  onClick?: () => void
  restrictionMessage?: string
} & React.ComponentProps<NavLink>

function SideNavigationLink({
  to,
  children: label,
  matchUrl,
  exact,
  onClick,
  restrictionMessage,
  ...props
}: Props) {
  const history = useHistory()
  const [isPressed, setPressed] = useState<boolean>(false)
  const lastTo = typeof to === 'function' ? to(commonRepo.router.location) : to
  const isTouchableDevice = isMobile || isTablet
  const isRestricted = !!restrictionMessage

  useEffect(() => {
    if (isPressed) {
      history.push(lastTo)
      setPressed(false)
    }
  }, [isPressed])

  // mousedown/touchstart: SPA navigation을 즉시 트리거하기 위한 진입점.
  // 권한 제한 시 안내 토스트를 띄우고 navigation을 중단한다.
  const handleClickStart = (e) => {
    e.preventDefault()
    if (isRestricted) {
      toastFailure(restrictionMessage!)
      return
    }
    onClick?.()
    setPressed(true)
  }

  // click: NavLink의 기본 a 태그 navigation을 막는 전용 가드.
  // mousedown의 preventDefault는 click을 막지 않으므로 권한 제한 시 별도로 차단해야 한다.
  const handleClick = (e) => {
    if (isRestricted) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return (
    <NavLink
      exact={exact}
      data-testid={`navigation-${label}`}
      data-component={`${label}탭`}
      to={to}
      css={css(S.defaultStyle)}
      activeStyle={activeStyle}
      isActive={(match, location) => {
        const customMatch = matchPath(location.pathname, { path: matchUrl, exact })
        return isPressed || (matchUrl ? !!customMatch : !!match)
      }}
      onTouchStart={isTouchableDevice ? handleClickStart : undefined}
      onMouseDown={!isTouchableDevice ? handleClickStart : undefined}
      onClick={handleClick}
      {...props}
    >
      {label}
    </NavLink>
  )
}

const S = {
  defaultStyle: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 54px;
    ${typo.body01};
    color: var(--Colors-Gray-600);
  `,

  newLabel: css`
    position: absolute;
    top: 50%;
    right: -16px;
    transform: translateY(-50%);

    img {
      border-radius: var(--Radius-100);
    }
  `,
}

const activeStyle = {
  fontWeight: '700',
  color: 'var(--Colors-Gray-900)',
}

export default SideNavigationLink
